import { useT } from '../lib/i18n'
import styles from './Wordmark.module.css'

/**
 * HEIMAR, and underneath it what the word means — in the reader's own language.
 *
 * Testers kept asking what the name is (founder, 2026-08-21), and the answer is worth one line:
 * *heimar* is the plural of *heimur* — worlds. The gloss says it without a page about it.
 *
 * ⚠ IT IS A DEFINITION, NOT A TAGLINE, and the whole design of the line is that defence. A line
 * under a wordmark wants very badly to become a slogan, and a slogan is marketing furniture —
 * the wrong register for this product entirely. Three things keep it a dictionary entry:
 *
 *   1. **The language label.** "Icelandic · worlds" cannot be read as a claim; "worlds" alone
 *      can. The label is the prefix that does the founder's job without being a locale code —
 *      `en —` is developer furniture under a wordmark; a language name is the actual convention.
 *   2. **No verb, no punctuation, no promise.** A noun and its language. The moment it reads
 *      "worlds, alive" it has become an advertisement.
 *   3. **Typographic subordination** — the meta face against the mark's display face, so it is
 *      visibly a different KIND of text rather than a smaller line of the same kind.
 *
 * ⚠ ICELANDIC SHOWS NOTHING, and that is the design rather than a gap. *Heimar* is already
 * their word; an Icelandic reader needs neither half of "Icelandic · worlds". The silence is
 * the product knowing who it is talking to — the same posture as paying for the heavier model
 * for that audience. A gloss there would have to read *heimar · heimar*.
 *
 * ⚠ THE GLOSS WORDS ARE BRAND COPY — the founder's pen. They live in the locale dictionary
 * (`mark.gloss`) because they are per-language, not because they are chrome.
 */
export default function Wordmark({
  variant = 'splash',
  className,
}: {
  /** 'splash' is the arrival; 'shelf' is the smaller return-visit mark. */
  variant?: 'splash' | 'shelf'
  className?: string
}) {
  const t = useT()
  const gloss = t('mark.gloss')
  const source = t('mark.glossSource')

  return (
    <div
      className={[styles.mark, variant === 'shelf' && styles.shelf, className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.word}>HEIMAR</div>
      {/* Empty in Icelandic — rendered as nothing at all rather than an empty box, so the mark
          sits at the same optical weight either way. */}
      {gloss && (
        <div className={styles.gloss}>
          {gloss}
          {/* HIERARCHY BY RECEDING THE ASIDE, not by weighting the word. Bold on "worlds" would
              read as emphasis, emphasis reads as a claim, and a claim is the tagline this line
              exists to avoid. Dimming the attribution ranks the two halves while keeping the
              whole line quieter than the mark — which is the point of it. The parentheses are
              the component's, so a translator supplies two plain facts and no punctuation. */}
          {source && <span className={styles.source}> ({source})</span>}
        </div>
      )}
    </div>
  )
}
